import { ToastOptions, useIonAlert, UseIonToastResult } from '@ionic/react';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { v4 } from 'uuid';
import odoo from './odoo';
import { atomWithProxy } from 'jotai-valtio';
import { proxy, subscribe } from 'valtio/vanilla';
import { updateUserId } from './Notification';
import { OSNotification } from 'onesignal-cordova-plugin/dist/OSNotification';
import { OnlineStatusContextType } from '../hooks/useOnlineStatus';
import { Geolocation, Position } from '@capacitor/geolocation';
import { closeOutline } from 'ionicons/icons';
import { Preferences } from '@capacitor/preferences';
import { each } from 'lodash/fp';
import { History, LocationState } from 'history';
import { setUser as setSentryUser } from '@sentry/capacitor';

export type AppConfig = {
    appMode: 'live' | 'manual';
    limitLunchToOne: boolean;
    roundingHours: number;
    limitPointingByDay: number;
};

export type OdooUserType = 'geiq' | 'adherent' | 'employee' | false;

export interface User {
    uid: number;
    login: string;
    jwt: string;
    baseUrl: string;
    db: string;
    type: OdooUserType;
    dbuuid: string;
}

export interface UserWithTypeText extends User {
    typeText: string;
}

export const getUserTypeText = (type: OdooUserType): string => {
    switch (type) {
        case 'adherent':
            return 'Adhérent';
        case 'geiq':
            return 'GEIQ';
        case 'employee':
            return 'Employé';
        default:
            return 'Utilisateur';
    }
};

export const proxyUsers = proxy<{ mainUser: User | null }>({ mainUser: null });
export const proxyOsNotification = proxy<{ notification: OSNotification | null }>({ notification: null });

export let usersAtom = atomWithProxy(proxyUsers);

// Derived atom that includes typeText property
export const usersWithTypeTextAtom = atom((get) => {
    const users = get(usersAtom);

    return {
        ...users,
        mainUser: users.mainUser
            ? ({
                  ...users.mainUser,
                  typeText: getUserTypeText(users.mainUser.type),
              } as UserWithTypeText)
            : null,
    };
});

export let osNotificationAtom = atomWithProxy(proxyOsNotification);

export const loadingAtom = atom(false);
export const toastsAtom = atom<ToastOptions & { uuid: string }[]>([]);
export const addToastAtom = atom(null, (_get, set, toast: ToastOptions) => {
    set(toastsAtom, (toasts) => [...toasts, { ...toast, uuid: v4() }]);
});
export const fallbackErrorCountAtom = atomWithStorage('fallbackErrorCount', 0);
export const pointingToEditIdAtom = atom<number | null>(null);
export const onlineStatusAtom = atom<OnlineStatusContextType>(navigator.onLine ? 'online' : 'offlineNotInit');
export const allowLocationAtom = atom(false);

subscribe(proxyUsers, () => {
    Preferences.set({ key: 'users', value: JSON.stringify(proxyUsers) });
    odoo.activeUser = proxyUsers.mainUser;
    updateUserId(proxyUsers.mainUser);
    setSentryUser({ id: proxyUsers.mainUser?.uid, username: proxyUsers.mainUser?.login });
});

export const getLocation = async (
    options: PositionOptions = { enableHighAccuracy: true }
): Promise<Position | null> => {
    try {
        return await Geolocation.getCurrentPosition(options);
    } catch (e) {
        console.log(e);
        return null;
    }
    return null;
};
subscribe(proxyOsNotification, () => {
    Preferences.set({ key: 'notification', value: JSON.stringify(proxyOsNotification) });
});

export const defaultToastOptions: ToastOptions = {
    duration: 3000,
    color: 'success',

    position: 'top',
    buttons: [
        {
            icon: closeOutline,
            role: 'cancel',
        },
    ],
};

export const defaultCatchOdooError = (error: any, presentToast: UseIonToastResult[0]) => {
    if (error.messages) {
        each((errorToast: ToastOptions) => presentToast(errorToast), error.messages);
    } else {
        presentToast({
            ...defaultToastOptions,
            message: error.message,
            color: 'danger',
        });
    }
};

export const init = async () => {
    const users = await Preferences.get({ key: 'users' });
    if (users.value) {
        proxyUsers.mainUser = JSON.parse(users.value)?.mainUser || null;
    }

    const notification = await Preferences.get({ key: 'notification' });
    if (notification.value) {
        proxyOsNotification.notification = JSON.parse(notification.value)?.notification || null;
    }

    // Reinit atomWithProxy to prevent bug when component is mount it has old value of proxy and immediately rerender and update (bug with routing /logout for example)
    usersAtom = atomWithProxy(proxyUsers);
    osNotificationAtom = atomWithProxy(proxyOsNotification);
};

export const goBackOrReplace = (history: History<LocationState>, path: string) => {
    if (history.length > 1) {
        history.goBack();
    } else {
        history.replace(path);
    }
};

export const useResetAlert = () => {
    const [presentAlert] = useIonAlert();

    const handleResetAlert = () => {
        presentAlert({
            header: "Réinitialiser l'application",
            message:
                "Cette action va supprimer toutes les données de l'application. Êtes-vous sûr de vouloir continuer ?",
            buttons: [
                'Annuler',
                {
                    text: 'Réinitialiser',
                    handler: async () => {
                        await Preferences.clear();
                        window.location.reload();
                    },
                },
            ],
        });
    };

    return handleResetAlert;
};
